/**
 * What: Mounts the new app-home HomePage on the real navigation, wired to live TestFeed data.
 * Transport / data-flow: Builds a HomeApi from the store-intelligence/usage hooks and the existing
 *   connect mutation already used by the old ShopifyHomePage. Home loads the store-sync status itself:
 *   only Home and Settings show it, so no other page polls it.
 */
import { useCallback, useState } from 'react'
import { productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { useStoreIntelligence } from '../../hooks/useStoreIntelligence'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import type { StoreSyncStatus } from '@testfeed/shopify-contracts'
import { useTestFeedUsage } from '../../hooks/useTestFeedUsage'
import { HomeEnvContext } from './useHome'
import { HomePage } from './HomePage'
import type { HomeApi } from './api'
import type { ConnectState, HomePrefs, LoadState, PlanState, SyncState } from './types'
import { resolveMerchantErrorMessage } from '../../merchantErrors'
import { useShopifyPlanSelectionUrl } from '../../hooks/useShopifyPlanSelectionUrl'
import { useShopifyPlanSync } from '../../hooks/useShopifyPlanSync'
import { requestShopifyNavigation, type NavigationClient } from '../../shopifyNavigation'

type BuildProgress = StoreSyncStatus['buildProgress']

const PREFS_STORAGE_KEY = 'testfeed:home-prefs'
// Placeholder until the sync pipeline exposes its own "sparse data" signal to the frontend;
// below this many synced orders the page tells the merchant shoppers also draw on similar stores.
const FEW_ORDERS_THRESHOLD = 50

function readPrefs(): HomePrefs {
  const fallback: HomePrefs = {
    guideDismissed: false,
    guideCollapsed: false,
    heroDismissed: false,
    dismissedBanners: [],
  }
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY)
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback
  } catch (e) {
    console.error(e)
    return fallback
  }
}
function writePrefs(prefs: HomePrefs) {
  try {
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs))
  } catch (e) {
    // Storage can be blocked; prefs just won't persist across visits.
    console.error(e)
  }
}

function stageCount(buildProgress: BuildProgress | undefined, kind: string): number | null {
  const stage = buildProgress?.stages.find((candidate) => candidate.kind === kind)
  return stage?.objectCount ?? null
}

function deriveSync(storeIntelligence: ReturnType<typeof useStoreIntelligence>): SyncState {
  const response = storeIntelligence.syncStatusResponse
  const stages = response?.buildProgress?.stages
  const dataUsable =
    response?.status === 'completed' ||
    !!response?.coverage?.publicationId ||
    ['orders', 'customers'].every((kind) => stages?.some((stage) => stage.kind === kind && stage.usable))
  if (dataUsable) {
    const orders = stageCount(response?.buildProgress, 'orders')
    let refresh: Extract<SyncState, { status: 'done' }>['refresh']
    if (storeIntelligence.isSyncing) refresh = { status: 'running', progress: null }
    else if (storeIntelligence.syncError) refresh = { status: 'failed', reason: storeIntelligence.syncError }
    return {
      status: 'done',
      // Customer rows are not a count of simulated shoppers. No such count or
      // last-sync timestamp is provided by this query.
      shoppers: null,
      orders,
      fewOrders: orders !== null && orders > 0 && orders < FEW_ORDERS_THRESHOLD,
      lastSynced: '',
      refresh,
    }
  }
  if (storeIntelligence.isSyncing) {
    // No merchant-facing percent is exposed yet; the page only needs to know work is happening.
    return { status: 'syncing', progress: null }
  }
  if (storeIntelligence.syncStatus === 'failed' || storeIntelligence.syncError) {
    return { status: 'failed', reason: storeIntelligence.syncError ?? productLanguage.shopify.sync.failed }
  }
  return { status: 'not_started' }
}

function deriveConnect(
  isTestFeedConnected: boolean,
  storeIntelligence: ReturnType<typeof useStoreIntelligence>,
): ConnectState {
  if (!isTestFeedConnected) return { status: 'needed' }
  const buildProgress = storeIntelligence.syncStatusResponse?.buildProgress
  return {
    status: 'granted',
    products: stageCount(buildProgress, 'products'),
    orders: stageCount(buildProgress, 'orders'),
  }
}

function derivePlan(usage: ReturnType<typeof useTestFeedUsage>['data']): PlanState {
  const balance = usage?.creditBalance
  if (!balance || balance.state === 'no_plan') return { status: 'none' }
  // The credit contract has no trial concept yet, so every configured plan reads as "active" —
  // avoids inventing a trial countdown the backend doesn't send.
  return {
    status: 'active',
    name: balance.plan.name,
    testsIncluded: balance.includedCredits,
    testsUsed: Math.max(0, balance.includedCredits - balance.availableCredits),
  }
}

export function HomeRoute({
  shopify,
  isTestFeedConnected,
  onConnect,
  storeScope,
  navigateToStudyBuilder,
  navigateToInsights,
  navigateToStudies,
  navigateToSettings,
  navigateToDirectResults,
}: {
  shopify: ShopifyAppBridge & NavigationClient
  isTestFeedConnected: boolean
  /** The same connect trigger ShopifyHomePage already uses (builder.handleConnectTestFeed). */
  onConnect: () => void
  /** The verified connection; Home loads the store-sync status for it. */
  storeScope: VerifiedTestFeedConnectionScope | null
  navigateToStudyBuilder: () => void
  navigateToInsights: () => void
  navigateToStudies: () => void
  navigateToSettings: () => void
  navigateToDirectResults: (studyId: string, runId: string | null) => void
}) {
  const storeIntelligence = useStoreIntelligence(shopify, { isTestFeedConnected, scope: storeScope ?? undefined })
  useShopifyPlanSync(shopify)
  const usageQuery = useTestFeedUsage(shopify)
  const planSelection = useShopifyPlanSelectionUrl(shopify, true)
  const [prefs, setPrefs] = useState(readPrefs)
  const statusQuery = storeIntelligence.syncStatusQuery

  // Queries own server data. A background read keeps its last accepted data;
  // only an initial missing response needs the shaped loading/error state.
  let state: LoadState = { kind: 'loading' }
  const readError = statusQuery.error ?? usageQuery.error
  if (statusQuery.data && usageQuery.data) {
    state = {
      kind: 'ready',
      data: {
        shopName: storeIntelligence.shopSettings?.shopName ?? '',
        connect: deriveConnect(isTestFeedConnected, storeIntelligence),
        plan: derivePlan(usageQuery.data),
        sync: deriveSync(storeIntelligence),
        // Home has no recent-tests source yet; do not fabricate results.
        tests: [],
        prefs,
      },
      stale: readError
        ? {
            savedAt: new Date(Math.min(statusQuery.dataUpdatedAt, usageQuery.dataUpdatedAt)).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            }),
          }
        : undefined,
    }
  } else if (readError) {
    state = {
      kind: 'error',
      message: resolveMerchantErrorMessage(readError, shopifyComponentCopy.appHome.loadErrorUnreachable),
    }
  }

  const api: HomeApi = {
    refresh: async () => {
      const results = await Promise.all([statusQuery.refetch(), usageQuery.refetch()])
      for (const result of results) if (result.isError) throw result.error
    },
    connect: async () => {
      onConnect()
    },
    choosePlan: async () => {
      const pricing = await planSelection.refetch()
      if (!pricing.data || pricing.isError) throw pricing.error ?? new Error('SHOPIFY_PLAN_LINK_UNAVAILABLE')
      const left = await requestShopifyNavigation(shopify, () => {
        globalThis.window?.open(pricing.data, '_top', 'noopener')
      })
      if (!left) throw new DOMException('Navigation cancelled', 'AbortError')
    },
    startSync: () => storeIntelligence.runSync(false),
    resync: () => storeIntelligence.runSync(true),
    savePrefs: async (patch) => {
      setPrefs((current) => ({ ...current, ...patch }))
      writePrefs({ ...readPrefs(), ...patch })
    },
  }

  const navigate = useCallback(
    (to: string) => {
      if (to.startsWith('/app/tests/new')) {
        navigateToStudyBuilder()
        return
      }
      if (to === '/app/tests') {
        navigateToStudies()
        return
      }
      if (to.startsWith('/app/tests/')) {
        // Recent tests row links. Home currently reports `tests: []` (no real "recent
        // tests" source is wired to Home yet), so this is unreached today — wired for when it is.
        navigateToDirectResults(to.slice('/app/tests/'.length), null)
        return
      }
      // Product insights, Insights, and Opportunities all live on the one real
      // Insights page today — there's no separate Opportunities route (see docs/retros for the
      // Product insights page removal).
      if (to === '/insights' || to === '/app/opportunities') {
        navigateToInsights()
        return
      }
      // "How we protect data" / "What we read" — there's no standalone privacy route; that
      // content lives in Settings' "Data and privacy" section.
      if (to === '/app/privacy') {
        navigateToSettings()
        return
      }
      console.error(`[HomeRoute] No real destination wired for "${to}"`)
    },
    [navigateToStudyBuilder, navigateToInsights, navigateToStudies, navigateToSettings, navigateToDirectResults],
  )

  const env = { state, api, navigate }

  return (
    <HomeEnvContext.Provider value={env}>
      <HomePage />
    </HomeEnvContext.Provider>
  )
}
